import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import type { JSX } from "react";
import { PageBreadcrumbs, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import {
  getGridBooleanOperators,
  getGridSingleSelectOperators,
  GridColDef,
  GridColTypeDef,
} from "@mui/x-data-grid-pro";
import { ProductCatalog } from "types/productCatalog";
import React from "react";
import { linkColDef } from "components/DataGrid/columnDefs/linkColDef";
import { Link } from "react-router-dom";
import amazonLogo from "components/ProductCatalogs/assets/amazon-logo.png";
import backmarketLogo from "components/ProductCatalogs/assets/backmarket_logo.png";
import ebayLogo from "components/ProductCatalogs/assets/ebay-logo.png";
import reebeloLogo from "components/ProductCatalogs/assets/reebelo-logo.png";
import { daysCountColDef } from "components/DataGrid/columnDefs/daysCountColDef";
import { dateTimeColDef } from "components/DataGrid/columnDefs/dateTimeColDef";
import { Button, Stack } from "@mui/material";
import { Add, Autorenew, CheckCircle, Error, RemoveCircle } from "@mui/icons-material";
import { ElasticDataGrid } from "components/DataGrid/RevivnElasticDataGrid";

const toPrice = (value: string | number) => Number(value).toFixed(2);

const priceColDef = (field: keyof ProductCatalog): GridColTypeDef<ProductCatalog> => {
  return {
    renderCell: (params) => <>{params.row[field] ? `$${toPrice(params.row[field])}` : "-"}</>,
  };
};
type StatusType = "active" | "action_needed" | "not_listed" | "pending" | "invalid";

const merchantStatus: Record<string, { icon: JSX.Element; label: string }> = {
  active: {
    icon: <CheckCircle fontSize="small" color="success" />,
    label: "Active",
  },
  actionNeeded: {
    icon: <Error fontSize="small" color="warning" />,
    label: "Action Needed",
  },
  pending: {
    icon: <Autorenew fontSize="small" color="warning" />,
    label: "Pending",
  },
  notListed: {
    icon: <RemoveCircle fontSize="small" color="disabled" />,
    label: "Not Listed",
  },
  invalid: {
    icon: <RemoveCircle fontSize="small" color="disabled" />,
    label: "Invalid",
  },
};

const statusMapping: Record<StatusType | "not_listed", { icon: JSX.Element; label: string }> = {
  active: merchantStatus.active,
  action_needed: merchantStatus.actionNeeded,
  invalid: merchantStatus.invalid,
  pending: merchantStatus.pending,
  not_listed: merchantStatus.notListed,
};

const statusFilters = {
  display: "flex",
  width: 140,
  filterOperators: getGridSingleSelectOperators(),
  valueOptions: [
    { value: "active", label: "Active" },
    { value: "action_needed", label: "Action Needed" },
    { value: "not_listed", label: "Not Listed" },
  ],
};

const photosColDef: GridColTypeDef<ProductCatalog> = {
  renderCell: (params) => {
    const productCatalog = params.row;
    const hasPhotos = productCatalog.hasStockPhotos || productCatalog.hasResalePhotos;

    if (!productCatalog.photosUrl) {
      return null;
    }

    return (
      <Link to={productCatalog.photosUrl} style={{ textDecoration: "none" }}>
        <Button size="small" variant="text" color="primary" startIcon={hasPhotos ? <CheckCircle /> : <Add />}>
          {hasPhotos ? "View Photos" : "Add Photos"}
        </Button>
      </Link>
    );
  },
};

const channelStatusColDef = (field: keyof ProductCatalog): GridColTypeDef<ProductCatalog> => {
  return {
    renderCell: (params) => {
      if (!params.row[field] && (field === "backmarketStatus" || field === "reebeloStatus")) {
        return "-";
      }

      const status = (params.row[field] as StatusType) || "not_listed";
      let icon, label;
      switch (field) {
        default:
          ({ icon, label } = statusMapping[status]);
          break;
      }

      return (
        <Stack direction="row">
          {icon}
          {label}
        </Stack>
      );
    },
  };
};

const columns: GridColDef<ProductCatalog>[] = [
  { field: "id", headerName: "ID" },
  {
    field: "merchantProductNumber",
    headerName: "MPN",
    ...linkColDef<ProductCatalog>(({ row }) => {
      return `/product-catalog/${row.id}`;
    }),
  },
  {
    field: "quantity",
    headerName: "Quantity",
    type: "number",
    renderCell: (params) => {
      if (params.row.quantity && params.row.quantity > 0) {
        const filter = `q=merchant_product_number:"${params.row.merchantProductNumber}"`;
        return <Link to={`/inventories?${filter}`}>{params.row.quantity}</Link>;
      }

      return null;
    },
  },
  { field: "deviceType", headerName: "Device Type" },
  { field: "title", headerName: "Title" },
  { field: "sku", headerName: "SKU" },
  {
    field: "amazonStatus",
    headerName: "Amazon Status",
    renderHeader: () => <img src={amazonLogo} style={{ width: "50%", height: "50%", paddingTop: "8px" }} />,
    ...channelStatusColDef("amazonStatus"),
    ...statusFilters,
  },
  {
    field: "backmarketStatus",
    headerName: "BackMarket Status",
    renderHeader: () => <img src={backmarketLogo} style={{ width: "80%", height: "80%" }} />,
    ...channelStatusColDef("backmarketStatus"),
    ...statusFilters,
  },
  {
    field: "ebayStatus",
    headerName: "Ebay Status",
    renderHeader: () => <img src={ebayLogo} style={{ width: "50%", height: "50%" }} />,
    ...channelStatusColDef("ebayStatus"),
    ...statusFilters,
  },
  {
    field: "reebeloStatus",
    headerName: "Reebelo Status",
    renderHeader: () => <img src={reebeloLogo} style={{ width: "50%", height: "50%" }} />,
    ...channelStatusColDef("reebeloStatus"),
    ...statusFilters,
    valueOptions: [
      { value: "active", label: "Active" },
      { value: "not_listed", label: "Not Listed" },
    ],
  },
  { field: "inventoryId", headerName: "Phoenix ID" },
  { field: "inventoryGroup", headerName: "Inventory Group" },
  { field: "dynamicInventoryGroup", headerName: "Dynamic Inventory Group" },
  { field: "backmarketId", headerName: "Backmarket ID" },
  { field: "reebeloId", headerName: "Reebelo ID" },
  {
    field: "amazonPriceInCents",
    headerName: "Amazon Price",
    ...priceColDef("amazonPrice"),
    type: "number",
  },
  {
    field: "ebayPriceInCents",
    headerName: "Ebay Price",
    ...priceColDef("ebayPrice"),
    type: "number",
  },
  {
    field: "startingPriceInCents", // TODO: Remove field
    headerName: "Starting Price",
    ...priceColDef("startingPrice"),
    type: "number",
  },
  {
    field: "backmarketPriceInCents",
    headerName: "Backmarket Price",
    ...priceColDef("backmarketPrice"),
    type: "number",
  },
  {
    field: "reebeloPriceInCents",
    headerName: "Reebelo Price",
    ...priceColDef("reebeloPrice"),
    type: "number",
  },
  {
    field: "lastSaleAt",
    headerName: "Days since sold",
    ...daysCountColDef,
    filterable: true,
    sortable: true,
    type: "number",
    width: 150,
  },
  {
    field: "currentPriceLastSaleAt",
    headerName: "Days since sold at price",
    ...daysCountColDef,
    filterable: true,
    sortable: true,
    type: "number",
    width: 150,
  },
  {
    field: "lastAmazonSaleAt",
    headerName: "Days since sold in Amazon",
    ...daysCountColDef,
    filterable: true,
    sortable: true,
    type: "number",
    width: 150,
  },
  {
    field: "lastEbaySaleAt",
    headerName: "Days since sold in Ebay",
    ...daysCountColDef,
    filterable: true,
    sortable: true,
    type: "number",
    width: 150,
  },
  {
    field: "lastBackmarketSaleAt",
    headerName: "Days since sold in Backmarket",
    ...daysCountColDef,
    filterable: true,
    sortable: true,
    type: "number",
    width: 150,
  },
  {
    field: "lastReebeloSaleAt",
    headerName: "Days since sold in Reebelo",
    ...daysCountColDef,
    filterable: true,
    sortable: true,
    type: "number",
    width: 150,
  },
  {
    field: "ean",
    headerName: "EAN",
    filterable: true,
    sortable: true,
    type: "string",
  },
  { field: "badBattery", headerName: "Bad Battery", filterOperators: getGridBooleanOperators() },
  { field: "nonFunctional", headerName: "Non Functional", filterOperators: getGridBooleanOperators() },
  { field: "subtitle", headerName: "Subtitle", hide: true, filterable: false },
  { field: "description", headerName: "Description", hide: true, filterable: false },
  { field: "categoryId", headerName: "Category ID", hide: true, filterable: false },
  { field: "primaryCategory", headerName: "Primary Category", hide: true, filterable: false },
  { field: "secondaryCategory", headerName: "Secondary Category", hide: true, filterable: false },
  { field: "condition", headerName: "Condition" },
  { field: "conditionNote", headerName: "Condition Note", hide: true },
  { field: "brand", headerName: "Brand" },
  { field: "screenSize", headerName: "Screen Size" },
  { field: "processor", headerName: "Processor" },
  { field: "modelDescription", headerName: "Model" },
  { field: "model", headerName: "Model Order" },
  { field: "releaseYear", headerName: "Release Year" },
  { field: "hardDriveCapacity", headerName: "Hard Drive Capacity", hide: true },
  { field: "color", headerName: "Color" },
  { field: "ram", headerName: "RAM" },
  { field: "storageType", headerName: "Storage Type", hide: true, filterable: false },
  { field: "gpu", headerName: "GPU" },
  { field: "processorSpeed", headerName: "Processor Speed" },
  { field: "operatingSystem", headerName: "Operating System", hide: true, filterable: false },
  { field: "maximumResolution", headerName: "Maximum Resolution", hide: true, filterable: false },
  { field: "keyboardLanguage", headerName: "Keyboard Language" },
  { field: "itemHeight", headerName: "Item Height", hide: true, filterable: false },
  { field: "itemWidth", headerName: "Item Width", hide: true, filterable: false },
  { field: "itemLength", headerName: "Item Length", hide: true, filterable: false },
  { field: "itemWeight", headerName: "Item Weight", hide: true, filterable: false },
  { field: "shippingPolicyName", headerName: "Shipping Policy Name", hide: true, filterable: false },
  { field: "daysOnShelf", headerName: "Days On Shelf", hide: true },
  { field: "daysSincePurchase", headerName: "Days Since Purchase", hide: true },
  {
    field: "createdAt",
    headerName: "Created",
    ...dateTimeColDef,
    filterable: false,
    width: 200,
  },
  {
    field: "updatedAt",
    headerName: "Last Updated",
    ...dateTimeColDef,
    filterable: false,
    width: 200,
  },
  {
    field: "lastQuantitiesSyncedAt",
    headerName: "Last Quantities Sync",
    ...dateTimeColDef,
    filterable: false,
    width: 200,
  },
  {
    field: "lastFieldsSyncedAt",
    headerName: "Last Fields Sync",
    ...dateTimeColDef,
    filterable: false,
    width: 200,
  },
  {
    field: "photosUrl",
    headerName: "Photos",
    ...photosColDef,
    filterable: false,
    sortable: false,
    width: 200,
  },
];

export const ElasticProductCatalogPage = () => {
  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Resale" }]} />
      <PageTitle>Product Catalog</PageTitle>
      <ElasticDataGrid
        autoHeight={false}
        checkboxSelection={false}
        columns={columns}
        density="compact"
        defaultToMostRecentParams
        syncStateWithSearchParams
        persistSettingsKey={"inventories"}
        url={"/product_catalogs/elastic_index"}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
              itemWidth: false,
              itemHeight: false,
              itemLength: false,
              itemWeight: false,
            },
          },
        }}
      />
    </FullPagePaperLayout>
  );
};
